import { brandApi } from "../lib/axios";
import { AppearanceSettings, BannerCrop, BrandDetails } from "../types/brand";
import { Niche } from "../types/niche";
import { api } from "../lib/axios";
import { PaginatedResponse } from "./program.service";
import type { BrandDashboardTables } from "../types/dashboard";
import {
  AgencyBrandRequest,
  RespondToBrandRequestPayload,
} from "./agency.service";

interface UpdatePersonalDetailsPayload {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  timezone: string;
  password?: string;
  profileImage?: string;
}

const MOCK_SOCIAL = {
  facebook: "#",
  instagram: "#",
  tiktok: "#",
  youtube: "#",
};

export interface BulkInviteSettings {
  bulkImportEnabled: boolean;
  maxRecipientsPerImport: number;
  maxFileSizeMb: number;
}

export type BulkInviteCampaignStatus =
  | "QUEUED"
  | "SENDING"
  | "WAITING"
  | "HALTED"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export interface BulkInviteCampaignSummary {
  id: string;
  status: BulkInviteCampaignStatus;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  bouncedCount: number;
  cancelledCount: number;
  createdAt: string;
  completedAt: string | null;
  haltedAt: string | null;
  haltReason: string | null;
}

export interface BulkInviteCampaignsOverview {
  totals: {
    campaignCount: number;
    totalRecipients: number;
    sentCount: number;
    failedCount: number;
    bouncedCount: number;
    cancelledCount: number;
  };
  campaigns: BulkInviteCampaignSummary[];
}

export interface BrandRewardType {
  id: string;
  reward: string;
  rewardTypes: string[];
  revenueThreshold: number;
}

export interface WonBrandReward {
  id: string;
  reward: string;
  rewardName: string;
  revenueAmount: number;
  rewardStatus: "PENDING" | "DELIVERED" | "CANCELLED";
  earnedAt: string;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
  rewardType: BrandRewardType;
}

export interface UnachievedBrandReward {
  id: string;
  reward: string;
  rewardTypes: string[];
  revenueThreshold: number;
}

export interface BrandRewardData {
  wonRewards: WonBrandReward[];
  unachievedRewards: UnachievedBrandReward[];
}

export interface OnboardingStatusResponse {
  programsStat: {
    totalPrograms: number;
    activePrograms: number;
    hasPrograms: boolean;
  };
  appearanceStat: {
    hasCustomAppearance: boolean;
  };
  campaignStats: {
    totalCampaigns: number;
  };
  affiliateStat: {
    totalAffiliates: number;
    hasAffiliates: boolean;
  };
  postCheckoutStat: {
    hasPostCheckoutConfig: boolean;
  };
  socialsStat: {
    hasSocials: boolean;
  };
  companyProfileStat: {
    hasCompanyProfile: boolean;
  };
  resourcesStat: {
    hasResources: boolean;
  };
}

export interface BrandAnalyticsResponse {
  totalAffiliateRevenue: {
    totalRevenue: number;
    revenueByDay: Record<string, number>;
    previusRevenue: number;
    percentageChange: string;
  };
  revenuePerAffilate: {
    totalRevenue: number;
    revenueByDay: Record<string, number>;
    previusRevenue: number;
    percentageChange: number;
    revenuePerAffilate: number;
    previousRevenuePerAffilate: number;
  };
  totalAffilates: {
    totalAffilates: number;
    affiliateByDay: Record<string, number>;
    currentTotals: number;
    previousTotals: number;
    percentageChange: number;
  };
  totalAffiliateOrders: {
    totalOrders: number;
    ordersByDay: Record<string, number>;
    previusOrders: number;
    percentageChange: string;
  };
  ordersPerAffiliate: {
    revenue: number;
    revenueByDay: Record<string, number>;
    percentageChange: string;
  };
  amountPayed: {
    amountPayed: number;
    amountByDay: Record<string, number>;
    percentageChange: number;
    previousPayedAmount: number;
  };
  averagePerAffiliate: {
    averagePayoutPerAffiliate: number;
    previousAveragePayoutPerAffiliate: number;
    percentageChange: number;
    averageByDay: Record<string, number>;
  };
  codeUses: {
    codeUses: number;
    codeUsesByDay: Record<string, number>;
    percentageChange: number;
    previousCodeUses: number;
  };
  countriesDistribution: {
    countriesDistribution: any;
    totalAffiliates: number;
  };
  totalCountries: {
    totalCountries: number;
    previousTotalCountries: number;
    percentageChange: number;
  };
  brandReward: BrandRewardData;
  campaignStats: {
    totalCampaigns: number;
    activeCampaigns: number;
    inactiveCampaigns: number;
    expiredCampaigns: number;
    percentageDiscountCampaigns: number;
    fixedAmountDiscountCampaigns: number;
  };
  postCheckoutAffiliates: {
    totalPostCheckoutAffiliates: number;
    currentTotals: number;
    previousTotals: number;
    percentageChange: number;
  };
}

export interface AffiliateDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phoneNumber: {
    countryCode: string;
    number: string;
    fullNumber: string;
  };
  gender: string;
  age: number;
  profileImage: string;
  username: string;
  bio: string;
  timezone: string;
  isActive: boolean;
  isEmailVerified: boolean;
  niches: Niche[];
  paypalEmail: string;
  cashApp: string;
  venmo: string;
  socialLinks: Array<{
    platform: string;
    username: string;
  }>;
}

export interface SocialMediaPlatform {
  id: string;
  name: string;
  url: string;
  iconUrl: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AffiliateSocialMedia {
  id: string;
  username: string;
  socialMediaPlatformId: string;
  affiliateId: string;
  createdAt: string;
  updatedAt: string;
  socialMediaPlatform: SocialMediaPlatform;
}

export interface AffiliateOverviewResponse {
  affiliate: {
    firstName: string;
    lastName: string;
    email: string;
    country: string;
    phoneNumber: string;
    gender: string;
    age: number;
    profileImage: string;
    username: string;
    bio: string;
    id: string;
    niches: Niche[];
    AffiliateSocialMedia: AffiliateSocialMedia[];
  };
  analytics: {
    commision: {
      commision: number;
      commisionByDay: { [key: string]: number };
      persentageChange: number;
      previousCommission: number;
    };
    revenue: {
      totalSales: number;
      salesByDay: { [key: string]: number };
      persentageChange: number;
      previousRevenue: number;
    };
    orders: {
      orders: number;
      orderByDay: { [key: string]: number };
      persentageChange: number;
      previousOrders: number;
    };
    salesByBrand: {
      salesByBrand: Array<{
        brand: string;
        totalSales: number;
      }>;
    };
  };
}

/** GET /analytics/brand/affiliates/:id?days=N. Mirrors the backend's
 *  BrandAffiliateOverview in src/analytics/brand-affiliate.service.ts: one
 *  affiliate as this brand sees them, scoped to the brand's own programs. */
export interface BrandAffiliateOverview {
  period: { from: string; to: string; days: number };
  affiliate: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string | null;
    country: string | null;
    profileImage: string | null;
    bio: string | null;
    createdAt: string;
    niches: { id: string; name: string }[];
    socials: { id: string; platform: string; username: string }[];
  };
  enrollments: {
    programId: string;
    programName: string;
    code: string;
    status: string;
    joinedAt: string;
    lastOrderAt: string | null;
    orders: number;
    revenue: number;
    commission: number;
  }[];
  current: { revenue: number; orders: number; commission: number; averageOrder: number };
  previous: { revenue: number; orders: number; commission: number };
  byDay: {
    revenue: Record<string, number>;
    orders: Record<string, number>;
    commission: Record<string, number>;
  };
  allTime: {
    revenue: number;
    orders: number;
    commission: number;
    averageOrder: number;
    firstSaleAt: string | null;
    lastSaleAt: string | null;
    shareOfPeriodRevenue: number;
  };
  payouts: { paid: number; pending: number; lastPaidAt: string | null };
  topProducts: { productId: string; title: string; image: string | null; units: number; revenue: number }[];
  recentSales: {
    id: string;
    orderNumber: string;
    orderStatusUrl: string | null;
    totalAmount: number;
    commissionAmount: number;
    codeUsed: string | null;
    status: string;
    createdAt: string;
    programName: string;
  }[];
}

export interface AffiliateOverview extends AffiliateDetails {
  stats: {
    totalRevenue: number;
    totalCommission: number;
    totalSales: number;
    averageOrderValue: number;
    conversionRate: number;
  };
  analytics: {
    date: string;
    revenue: number;
    commission: number;
    sales: number;
  }[];
}

export interface LatestAffiliateSale {
  id: string;
  orderId: string;
  date: string;
  code: string;
  orderTotal: string;
  affliateCommission: string;
}

export interface InvitedAffiliate {
  id: string;
  name: string;
  username: string;
  avatar: string;
  status: "PENDING" | "ACCEPTED";
  email: string;
  emailStatus: "SENT" | "PENDING";
}

export interface ProgramInvitation {
  id: string;
  affiliateId: string | null;
  email: string;
  programId: string;
  status: "PENDING" | "ACCEPTED" | "REJECTED";
  emailStatus: "SENT" | "FAILED";
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export const brandService = {
  async createPersonalDetails(data: unknown) {
    const response = await brandApi.post("/brand/personal-detail", data);
    return response.data;
  },

  async updatePersonalDetails(data: UpdatePersonalDetailsPayload) {
    const response = await brandApi.put("/brand/personal-detail", data);
    return response.data;
  },

  async updateCompanyDetails(data: unknown) {
    const response = await brandApi.post("/brand/company-detail", data);
    return response.data;
  },

  /** Re-frame the existing banner (Customizations › Banner framing). */
  async updateBannerFraming(data: {
    brandBannerImage: string;
    brandBannerImageMobile: string;
    brandBannerCrop: BannerCrop;
  }) {
    const response = await brandApi.put("/brand/banner", data);
    return response.data;
  },

  async getBrandDetails() {
    const response = await brandApi.get<BrandDetails>("/brand/detail");
    return response;
  },

  async updateProfileImage(imageUrl: string) {
    const response = await brandApi.patch("/brand/profile-image", { imageUrl });
    return response.data;
  },

  async getBrandAnalytics(days: number = 30): Promise<BrandAnalyticsResponse> {
    const response = await brandApi.get<BrandAnalyticsResponse>(
      `/analytics/brand/overview?days=${days}`
    );
    return response.data;
  },

  // affiliate brand
  async getAffiliateBrandDetails(shopId: string): Promise<BrandDetails> {
    const response = await api.get<BrandDetails>(`/brand/detail/${shopId}`);
    // Add only social mock data
    return {
      ...response.data,
      social: MOCK_SOCIAL,
    };
  },

  /**
   * @param programToken share token from a program join link (`?p=`). When it
   *   resolves, `data.program` is that program instead of the default one; when
   *   it doesn't, the default comes back with `programTokenInvalid: true`.
   */
  async getBrandDetailsByUsername(
    username: string,
    programToken?: string
  ): Promise<BrandDetails> {
    if(username === "surfs-up-candle-") {
      username= "surf's-up-candle-"
    }
    const response = await api.get<BrandDetails>(`/brand/detail/${username}`, {
      params: programToken ? { programToken } : undefined,
    });
    return {
      ...response.data,
    };
  },

  async getAffiliateDetails(id: string): Promise<AffiliateDetails> {
    return brandApi.get(`/affiliates/find-one/${id}`).then((res) => res.data);
  },

  async getAffiliateOverview(
    id: string,
    startDay: string,
    endDay: string,
    days: number
  ): Promise<AffiliateOverviewResponse> {
    return brandApi
      .get(`/affiliates/find-one-overview/${id}`, {
        params: {
          startDay,
          endDay,
          days,
        },
      })
      .then((res) => res.data);
  },

  /** One affiliate's figures with this brand only, for the last `days` days. */
  async getBrandAffiliateOverview(
    affiliateId: string,
    days: number = 30
  ): Promise<BrandAffiliateOverview> {
    const response = await brandApi.get<BrandAffiliateOverview>(
      `/analytics/brand/affiliates/${affiliateId}`,
      { params: { days } }
    );
    return response.data;
  },

  /** Top affiliates, top products, per-country figures and per-table
      summaries for the dashboard, for the last `days` days. */
  async getDashboardTables(days: number = 30): Promise<BrandDashboardTables> {
    const response = await brandApi.get<BrandDashboardTables>(
      "/analytics/brand/dashboard",
      { params: { days } }
    );
    return response.data;
  },

  async getLatestAffiliateSales(): Promise<LatestAffiliateSale[]> {
    return brandApi
      .get("/affiliate-sells/brand/latest")
      .then((res) => res.data);
  },

  async getInvitedAffiliates(
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<InvitedAffiliate>> {
    const response = await brandApi.get(`/program/invited-affiliates/ALL`, {
      params: { page, limit },
    });
    return response.data;
  },

  async bulkInviteAffiliates(emails: string[]): Promise<void> {
    await brandApi.post("/program/bulk-invite-affiliate", { emails });
  },

  async getBulkInviteSettings(): Promise<BulkInviteSettings> {
    const response = await brandApi.get("/program/bulk-invite/settings");
    return response.data;
  },

  async getBulkInviteCampaigns(): Promise<BulkInviteCampaignsOverview> {
    const response = await brandApi.get("/program/invite-campaigns");
    return response.data;
  },

  async removeAffiliate(affiliateId: string): Promise<void> {
    await brandApi.delete(`/program/remove-affiliate/${affiliateId}`);
  },

  // Agency-related methods
  async getBrandPendingRequests(): Promise<AgencyBrandRequest[]> {
    const response = await brandApi.get<AgencyBrandRequest[]>(
      "/agency/brand-requests/pending"
    );
    return response.data;
  },

  async respondToBrandRequest(
    requestId: string,
    data: RespondToBrandRequestPayload
  ): Promise<AgencyBrandRequest> {
    const response = await brandApi.post<AgencyBrandRequest>(
      `/agency/brand-requests/${requestId}/respond`,
      data
    );
    return response.data;
  },

  async getAppearanceSettings(): Promise<AppearanceSettings> {
    const response = await brandApi.get<AppearanceSettings>(
      "/brand-appearance/me"
    );
    return response.data;
  },

  async updateAppearanceSettings(
    data: Partial<AppearanceSettings>
  ): Promise<AppearanceSettings> {
    const response = await brandApi.put<AppearanceSettings>(
      "/brand-appearance/me",
      data
    );
    return response.data;
  },

  async getPostCheckoutSales(page: number = 1, limit: number = 10): Promise<{
    sales: Array<{
      id: string;
      orderId: string;
      orderNumber: string;
      orderStatusUrl: string | null;
      productTitle: string | null;
      totalPrice: number;
      commission: number;
      commissionType: string;
      commissionValue: string;
      discountCode: string;
      status: string;
      createdAt: string;
      affiliate: {
        id: string;
        name: string;
        email: string;
      };
      isMigrated: boolean;
    }>;
    stats: {
      totalSales: number;
      totalCommissions: number;
      totalOrders: number;
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await brandApi.get('/brand/post-checkout-sales', {
      params: { page, limit },
    });
    return response.data;
  },

  async getIncompletePostCheckoutAffiliates(
    page: number = 1,
    limit: number = 10,
    params: {
      searchBy?: "NAME" | "EMAIL" | "CODE";
      keyword?: string;
      countries?: string;
      gender?: "MALE" | "FEMALE" | "OTHER";
      niches?: string;
      minAge?: number;
      maxAge?: number;
      minRevenue?: number;
      maxRevenue?: number;
    } = {}
  ): Promise<{
    affiliates: Array<{
      id: string;
      name: string;
      email: string;
      code: string;
      accountStatus: string;
      createdVia: string;
      createdAt: string;
      lastInviteSentAt: string | null;
      reminderCount: number;
      totalSales: number;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams: Record<string, string | number> = {
      page,
      limit,
    };

    if (params.searchBy) {
      queryParams.searchBy = params.searchBy;
    }
    if (params.keyword) {
      queryParams.keyword = params.keyword;
    }
    if (params.countries) {
      queryParams.countries = params.countries;
    }
    if (params.gender) {
      queryParams.gender = params.gender;
    }
    if (params.niches) {
      queryParams.niches = params.niches;
    }
    if (params.minAge !== undefined) {
      queryParams.minAge = params.minAge;
    }
    if (params.maxAge !== undefined) {
      queryParams.maxAge = params.maxAge;
    }
    if (params.minRevenue !== undefined) {
      queryParams.minRevenue = params.minRevenue;
    }
    if (params.maxRevenue !== undefined) {
      queryParams.maxRevenue = params.maxRevenue;
    }

    const response = await brandApi.get(
      "/brand/post-checkout-affiliates/incomplete",
      {
        params: queryParams,
      }
    );
    return response.data;
  },

  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    const response = await brandApi.get<OnboardingStatusResponse>(
      `/analytics/brand/onboarding`
    );
    return response.data;
  },
};
